import { createExplorerControls } from '../../vendor/lifeworld-shared-base/src/index.js';
import {
  AppState,
  ClockId,
  EVIDENCE_MARKUP,
  EVIDENCE_TEXT,
  EvidenceKey,
  OBJECTS,
  WORLD,
  clamp,
  coreEvidenceCount,
  dist,
  initialClocks,
  lerp
} from './model.js';
import { WorldRenderer } from './render.js';

type ActionContext =
  | { kind: 'touch'; label: string }
  | { kind: 'microscope'; label: string }
  | { kind: 'return'; label: string }
  | null;

type TimeFocus = { id: ClockId; label: string; x: number; y: number; r: number } | null;

const query = <T extends Element>(selector: string): T => {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`LifeWorld A: missing required element ${selector}`);
  return element;
};

export function mountLifeWorld(): void {
  const canvas = query<HTMLCanvasElement>('#world');
  const app = query<HTMLElement>('#app');
  const missionPrompt = query<HTMLElement>('#missionPrompt');
  const nearHint = query<HTMLElement>('#nearHint');
  const softHint = query<HTMLElement>('#softHint');
  const evidenceChip = query<HTMLElement>('#evidenceChip');
  const timeBtn = query<HTMLButtonElement>('#timeBtn');
  const timeLabel = query<HTMLElement>('#timeLabel');
  const modelBadge = query<HTMLElement>('#modelBadge');
  const joystick = query<HTMLElement>('#joystick');
  const joyKnob = query<HTMLElement>('#joystickKnob');
  const actionBtn = query<HTMLButtonElement>('#actionBtn');

  const context = canvas.getContext('2d');
  if (!context) throw new Error('LifeWorld A: Canvas 2D context is unavailable');
  const ctx = context;
  const renderer = new WorldRenderer(ctx);

  const state: AppState = {
    w: 0,
    h: 0,
    dpr: 1,
    last: performance.now(),
    mode: 'world',
    player: { x: 560, y: 1590, vx: 0, vy: 0, dir: -0.7 },
    camera: { x: 560, y: 1590, z: 0.7, tx: 560, ty: 1590, tz: 0.7 },
    clocks: initialClocks(),
    evidence: {},
    evidenceQueue: [],
    hintUntil: 0,
    mimosaTouchedAt: -99999,
    seedlingSeenAt: null,
    duckWatchMs: 0,
    duckFeedWatchMs: 0,
    duckRestWatchMs: 0,
    duckFamilyWatchMs: 0,
    duckWasteSeen: false,
    duck: {
      x: OBJECTS.duck.x,
      y: OBJECTS.duck.y,
      homeX: OBJECTS.duck.x,
      homeY: OBJECTS.duck.y,
      vx: 0,
      vy: 0,
      startledUntil: 0,
      swimmingAway: false
    },
    lithopsCompared: false,
    microscopeHintShown: false,
    microscopeOpenedAt: 0
  };

  let actionContext: ActionContext = null;
  let currentTimeFocus: TimeFocus = null;
  let timeHeld = false;
  let hintTimer = 0;

  const explorerControls = createExplorerControls({
    joystick,
    knob: joyKnob,
    actionButton: actionBtn,
    onAction: performAction
  });
  explorerControls.setActive(true);

  function baseZoom(): number {
    if (state.w < 520) return 0.70;
    if (state.w < 840) return 0.66;
    if (state.w < 1180) return 0.62;
    return 0.58;
  }

  function resize(): void {
    state.dpr = Math.min(devicePixelRatio || 1, 2);
    state.w = innerWidth;
    state.h = innerHeight;
    canvas.width = Math.round(state.w * state.dpr);
    canvas.height = Math.round(state.h * state.dpr);
    canvas.style.width = `${state.w}px`;
    canvas.style.height = `${state.h}px`;
    ctx.setTransform(state.dpr, 0, 0, state.dpr, 0, 0);
    state.camera.tz = baseZoom();
  }

  function evidenceFound(key: EvidenceKey): boolean {
    return state.evidence[key] === true;
  }

  function showHint(text: string, ms = 2400, concept = false): void {
    window.clearTimeout(hintTimer);
    softHint.classList.remove('show', 'concept');
    softHint.textContent = '';
    if (concept) {
      softHint.classList.add('concept');
      softHint.innerHTML = text;
    } else {
      softHint.textContent = text;
    }
    requestAnimationFrame(() => softHint.classList.add('show'));
    state.hintUntil = performance.now() + ms;
    hintTimer = window.setTimeout(() => {
      softHint.classList.remove('show', 'concept');
      softHint.textContent = '';
      state.hintUntil = 0;
    }, ms);
  }

  function queueEvidence(key: EvidenceKey): void {
    if (state.evidence[key]) return;
    state.evidence[key] = true;
    state.evidenceQueue.push(key);
    updateMissionUI();
  }

  function pumpEvidence(now: number): void {
    if (!state.evidenceQueue.length || now < state.hintUntil) return;
    const key = state.evidenceQueue.shift();
    if (!key) return;
    showHint(EVIDENCE_MARKUP[key] || EVIDENCE_TEXT[key], key === 'observe' ? 3200 : 2600, true);
  }

  function updateMissionUI(): void {
    const count = coreEvidenceCount(state.evidence);
    const all = Object.keys(state.evidence).length;
    evidenceChip.textContent = count ? `已记录 ${count} 种生命证据` : '观察记录';
    evidenceChip.classList.toggle('active', all > 0);

    if (state.mode === 'microscope') {
      missionPrompt.textContent = '用更小的尺度继续找共同证据。';
      return;
    }
    if (evidenceFound('cells')) {
      missionPrompt.textContent = '继续自由观察：同一种证据还能在哪些生物身上找到？';
    } else if (count >= 5) {
      missionPrompt.textContent = '证据已经不少了。沿主路去观察台，看看更小的尺度。';
    } else if (count >= 2) {
      missionPrompt.textContent = '继续比较。别只看外形，有些证据要靠时间才能出现。';
    } else {
      missionPrompt.textContent = '先别急着判断。沿小路观察、比较，找“活着”的证据。';
    }
  }

  function activateClock(id: ClockId): void {
    state.clocks[id].active = true;
    if (id === 'seedlings' && state.seedlingSeenAt === null) {
      state.seedlingSeenAt = performance.now();
    }
  }

  function worldObjects(): Array<{ id: string; x: number; y: number; r: number; clock?: ClockId }> {
    return [
      { ...OBJECTS.seedlings },
      { ...OBJECTS.mimosa },
      { x: 820, y: 650, r: 250, id: 'rockGarden', clock: 'lithops' },
      { ...OBJECTS.seed },
      { ...OBJECTS.flower },
      { x: state.duck.x, y: state.duck.y, r: OBJECTS.duck.r, id: 'duck', clock: 'duck' },
      { ...OBJECTS.duckNest },
      { ...OBJECTS.microscope }
    ];
  }

  function updateActivation(): void {
    if (state.mode !== 'world') return;
    for (const object of worldObjects()) {
      if (!object.clock) continue;
      if (dist(state.player.x, state.player.y, object.x, object.y) < object.r + 180) {
        activateClock(object.clock);
      }
    }
  }

  function resolveTimeFocus(): TimeFocus {
    if (state.mode !== 'world') return null;
    const candidates: Array<NonNullable<TimeFocus>> = [
      { id: 'seedlings', label: '按住观察', x: OBJECTS.seedlings.x, y: OBJECTS.seedlings.y, r: 330 },
      { id: 'lithops', label: '按住观察', x: 820, y: 650, r: 320 },
      { id: 'seed', label: '按住观察', x: OBJECTS.seed.x, y: OBJECTS.seed.y, r: 320 },
      { id: 'flower', label: '按住观察', x: OBJECTS.flower.x, y: OBJECTS.flower.y, r: 340 },
      { id: 'duck', label: '安静观察', x: OBJECTS.duck.x, y: OBJECTS.duck.y, r: 470 }
    ];
    let best: TimeFocus = null;
    let bestDistance = Infinity;
    for (const item of candidates) {
      const d = dist(state.player.x, state.player.y, item.x, item.y);
      if (d < item.r && d < bestDistance && state.clocks[item.id].t < 0.995) {
        best = item;
        bestDistance = d;
      }
    }
    return best;
  }

  function resolveAction(): ActionContext {
    if (state.mode === 'microscope') return { kind: 'return', label: '返回观察园' };

    const mimosaDistance = dist(state.player.x, state.player.y, OBJECTS.mimosa.x, OBJECTS.mimosa.y);
    if (mimosaDistance < OBJECTS.mimosa.r + 105) {
      return { kind: 'touch', label: '轻轻触碰' };
    }

    if (coreEvidenceCount(state.evidence) >= 5) {
      const d = dist(state.player.x, state.player.y, OBJECTS.microscope.x, OBJECTS.microscope.y);
      if (d < OBJECTS.microscope.r + 125) {
        return { kind: 'microscope', label: '显微观察' };
      }
    }

    return null;
  }

  function nearestLabel(): string {
    if (state.mode !== 'world') return '';
    let best = Infinity;
    let text = '';
    const candidates = [
      { x: OBJECTS.seedlings.x, y: OBJECTS.seedlings.y, r: 260, text: '两株幼苗很像，短暂一眼可能分不清。' },
      { x: OBJECTS.mimosa.x, y: OBJECTS.mimosa.y, r: 220, text: '这株植物的叶片可以轻轻触碰。' },
      { x: 820, y: 650, r: 300, text: '两个对象都像石头。多观察一会儿。' },
      { x: OBJECTS.seed.x, y: OBJECTS.seed.y, r: 260, text: '透明盒里有一粒种子。' },
      { x: OBJECTS.flower.x, y: OBJECTS.flower.y, r: 280, text: '这里的花会随着时间发生变化。' },
      { x: OBJECTS.duck.x, y: OBJECTS.duck.y, r: 490, text: '离鸭子太近会惊动它，保持一点距离观察。' },
      { x: OBJECTS.microscope.x, y: OBJECTS.microscope.y, r: 280, text: coreEvidenceCount(state.evidence) >= 5 ? '观察台已经可以使用。' : '先在园里多找几种生命证据，再回来看看。' }
    ];
    for (const item of candidates) {
      const d = dist(state.player.x, state.player.y, item.x, item.y);
      if (d < item.r && d < best) {
        best = d;
        text = item.text;
      }
    }
    return text;
  }

  function updateContextUI(): void {
    actionContext = resolveAction();
    explorerControls.setAction(actionContext ? {
      kind: actionContext.kind === 'touch' ? 'touch' : actionContext.kind === 'return' ? 'return' : 'observe',
      label: actionContext.label
    } : null);

    currentTimeFocus = resolveTimeFocus();
    timeBtn.classList.toggle('visible', Boolean(currentTimeFocus) && state.mode === 'world');
    timeBtn.classList.toggle('active', timeHeld && Boolean(currentTimeFocus));
    if (currentTimeFocus) timeLabel.textContent = currentTimeFocus.label;

    const label = nearestLabel();
    nearHint.textContent = label;
    nearHint.classList.toggle('show', Boolean(label) && state.mode === 'world');
  }

  function performAction(): void {
    actionContext = resolveAction();
    if (!actionContext) return;

    if (actionContext.kind === 'touch') {
      state.mimosaTouchedAt = performance.now();
      queueEvidence('stimulus');
      return;
    }
    if (actionContext.kind === 'microscope') {
      enterMicroscope();
      return;
    }
    returnToWorld();
  }

  function enterMicroscope(): void {
    window.clearTimeout(hintTimer);
    state.evidenceQueue.length = 0;
    state.hintUntil = 0;
    softHint.classList.remove('show', 'concept');
    softHint.textContent = '';

    state.mode = 'microscope';
    state.microscopeOpenedAt = performance.now();
    timeHeld = false;
    app.classList.add('microscope-mode');
    modelBadge.textContent = '显微示意 · 非真实颜色与比例';
    modelBadge.classList.add('show');

    if (!state.evidence.cells) state.evidence.cells = true;
    updateMissionUI();
    updateContextUI();
    showHint(EVIDENCE_MARKUP.cells || EVIDENCE_TEXT.cells, 3000, true);
  }

  function returnToWorld(): void {
    state.mode = 'world';
    app.classList.remove('microscope-mode');
    modelBadge.classList.remove('show');
    updateMissionUI();
    updateContextUI();
  }

  function inPond(x: number, y: number): boolean {
    const dx = (x - OBJECTS.pond.x) / 500;
    const dy = (y - OBJECTS.pond.y) / 338;
    return dx * dx + dy * dy < 0.94;
  }

  function updateMovement(dt: number): void {
    if (state.mode !== 'world') {
      state.player.vx = 0;
      state.player.vy = 0;
      explorerControls.updateVector();
      return;
    }

    const input = explorerControls.updateVector();
    const speed = 360;
    state.player.vx = lerp(state.player.vx, input.mx * speed, clamp(dt * 0.014));
    state.player.vy = lerp(state.player.vy, input.my * speed, clamp(dt * 0.014));

    const nx = clamp(state.player.x + state.player.vx * dt / 1000, 65, WORLD.w - 65);
    const ny = clamp(state.player.y + state.player.vy * dt / 1000, 65, WORLD.h - 65);

    if (!inPond(nx, ny)) {
      state.player.x = nx;
      state.player.y = ny;
    } else {
      state.player.vx *= 0.18;
      state.player.vy *= 0.18;
    }

    if (Math.hypot(input.mx, input.my) > 0.08) {
      state.player.dir = Math.atan2(input.my, input.mx);
    }
  }

  function updateCamera(dt: number): void {
    if (state.mode !== 'world') return;

    let tx = state.player.x;
    let ty = state.player.y;
    let tz = baseZoom();

    const focus = worldObjects()
      .map((o) => ({ ...o, d: dist(state.player.x, state.player.y, o.x, o.y) }))
      .filter((o) => o.d < (o.id === 'duck' ? 540 : o.r + 170))
      .sort((a, b) => a.d - b.d)[0];

    if (focus) {
      if (focus.id === 'duck') {
        // Keep both observer and animal in frame while preserving a respectful observation distance.
        tx = lerp(tx, focus.x, 0.62);
        ty = lerp(ty, focus.y, 0.40);
        tz -= 0.025;
      } else {
        const q = 1 - clamp((focus.d - focus.r * 0.25) / Math.max(130, focus.r + 120));
        tx = lerp(tx, focus.x, 0.18 * q);
        ty = lerp(ty, focus.y, 0.14 * q);
        tz += 0.035 * q;
      }
    }

    const halfW = Math.min(WORLD.w / 2, state.w / (2 * tz));
    const halfH = Math.min(WORLD.h / 2, state.h / (2 * tz));
    tx = clamp(tx, halfW, WORLD.w - halfW);
    ty = clamp(ty, halfH, WORLD.h - halfH);

    state.camera.tx = tx;
    state.camera.ty = ty;
    state.camera.tz = tz;

    const k = 1 - Math.pow(0.001, dt / 1000);
    state.camera.x = lerp(state.camera.x, tx, k * 0.72);
    state.camera.y = lerp(state.camera.y, ty, k * 0.72);
    state.camera.z = lerp(state.camera.z, tz, k * 0.58);
  }

  function updateClocks(dt: number): void {
    if (state.mode !== 'world') return;
    currentTimeFocus = resolveTimeFocus();

    for (const [id, clock] of Object.entries(state.clocks) as Array<[ClockId, AppState['clocks'][ClockId]]>) {
      if (!clock.active) continue;
      const focused = currentTimeFocus?.id === id;
      const rate = focused && timeHeld ? 0.00022 : 0.000006;
      clock.t = clamp(clock.t + dt * rate);
    }
  }

  function updateDuck(now: number, dt: number): void {
    if (state.mode !== 'world' || state.clocks.duck.t < 0.28) return;

    const d = dist(state.player.x, state.player.y, state.duck.x, state.duck.y);
    if (d < 215) {
      state.duck.swimmingAway = true;
      state.duck.startledUntil = now + 2400;
      const dx = state.duck.x - state.player.x;
      const dy = state.duck.y - state.player.y;
      const m = Math.hypot(dx, dy) || 1;
      state.duck.vx = dx / m * 280;
      state.duck.vy = dy / m * 240;
    }

    if (state.duck.swimmingAway) {
      state.duck.x += state.duck.vx * dt / 1000;
      state.duck.y += state.duck.vy * dt / 1000;
      state.duck.x = clamp(state.duck.x, 2050, 2790);
      state.duck.y = clamp(state.duck.y, 350, 870);

      if (now > state.duck.startledUntil && d > 360) {
        const dx = state.duck.homeX - state.duck.x;
        const dy = state.duck.homeY - state.duck.y;
        const m = Math.hypot(dx, dy) || 1;
        state.duck.vx = dx / m * 150;
        state.duck.vy = dy / m * 150;
        if (m < 30) {
          state.duck.x = state.duck.homeX;
          state.duck.y = state.duck.homeY;
          state.duck.vx = 0;
          state.duck.vy = 0;
          state.duck.swimmingAway = false;
        }
      }
    } else {
      state.duck.x = state.duck.homeX + Math.sin(now * 0.00045) * 42;
      state.duck.y = state.duck.homeY + Math.sin(now * 0.00058 + 1.4) * 24;
    }
  }

  function updateObservations(now: number, dt: number): void {
    if (state.mode !== 'world') return;

    const seedlingD = dist(state.player.x, state.player.y, OBJECTS.seedlings.x, OBJECTS.seedlings.y);
    if (seedlingD < OBJECTS.seedlings.r + 160) {
      activateClock('seedlings');
      if (state.clocks.seedlings.t > 0.48) queueEvidence('observe');
    }

    const rockD = dist(state.player.x, state.player.y, 820, 650);
    if (rockD < 320) {
      activateClock('lithops');
      if (!state.lithopsCompared && state.clocks.lithops.t > 0.72 && now >= state.hintUntil) {
        state.lithopsCompared = true;
        showHint('外形很像不等于性质一样：一个持续生长并开花，旁边的石块没有这种变化。', 3200);
      }
    }

    const seedD = dist(state.player.x, state.player.y, OBJECTS.seed.x, OBJECTS.seed.y);
    if (seedD < OBJECTS.seed.r + 190) {
      activateClock('seed');
      if (state.clocks.seed.t > 0.56) queueEvidence('growth');
    }

    const flowerD = dist(state.player.x, state.player.y, OBJECTS.flower.x, OBJECTS.flower.y);
    if (flowerD < OBJECTS.flower.r + 220) {
      activateClock('flower');
      if (state.clocks.flower.t > 0.82) queueEvidence('reproduction');
    }

    const duckD = dist(state.player.x, state.player.y, state.duck.x, state.duck.y);
    if (duckD < 560) activateClock('duck');

    const calm = state.clocks.duck.t > 0.45 && !state.duck.swimmingAway && duckD > 245 && duckD < 500;
    const cycle = (now % 12000) / 12000;
    const feeding = cycle > 0.16 && cycle < 0.42;

    if (calm) {
      state.duckWatchMs += dt;
      if (feeding) state.duckFeedWatchMs += dt;
      if (!feeding) state.duckRestWatchMs += dt;
    } else {
      state.duckWatchMs = Math.max(0, state.duckWatchMs - dt * 0.18);
      state.duckFeedWatchMs = Math.max(0, state.duckFeedWatchMs - dt * 0.20);
      state.duckRestWatchMs = Math.max(0, state.duckRestWatchMs - dt * 0.16);
    }

    if (state.duckFeedWatchMs > 700) queueEvidence('nutrition');
    if (state.duckRestWatchMs > 1900) queueEvidence('respiration');

    if (state.clocks.duck.t > 0.72 && evidenceFound('nutrition')) state.duckWasteSeen = true;
    if (state.duckWasteSeen && dist(state.player.x, state.player.y, 2280, 920) < 250) {
      queueEvidence('waste');
    }

    const familyD = dist(state.player.x, state.player.y, OBJECTS.duckNest.x, OBJECTS.duckNest.y);
    if (state.clocks.duck.t > 0.86 && familyD < 360 && !state.duck.swimmingAway) {
      state.duckFamilyWatchMs += dt;
    } else {
      state.duckFamilyWatchMs = Math.max(0, state.duckFamilyWatchMs - dt * 0.15);
    }
    if (state.duckFamilyWatchMs > 1000) queueEvidence('reproduction');
    if (state.duckFamilyWatchMs > 2600) queueEvidence('heredityVariation');

    if (coreEvidenceCount(state.evidence) >= 5 && !state.microscopeHintShown && now >= state.hintUntil) {
      state.microscopeHintShown = true;
      showHint('你已经找到多种“活着”的证据。主路尽头的观察台亮起来了。', 3000);
    }
  }

  function frame(now: number): void {
    const dt = Math.min(40, Math.max(0, now - state.last));
    state.last = now;

    updateMovement(dt);
    updateActivation();
    updateClocks(dt);
    updateDuck(now, dt);
    updateObservations(now, dt);
    updateCamera(dt);
    updateContextUI();
    pumpEvidence(now);

    renderer.draw(state, now);
    requestAnimationFrame(frame);
  }

  function stopTime(): void {
    timeHeld = false;
    timeBtn.classList.remove('active');
  }

  timeBtn.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    if (!currentTimeFocus) return;
    activateClock(currentTimeFocus.id);
    timeHeld = true;
    timeBtn.classList.add('active');
    timeBtn.setPointerCapture?.(event.pointerId);
  });
  timeBtn.addEventListener('pointerup', stopTime);
  timeBtn.addEventListener('pointercancel', stopTime);
  timeBtn.addEventListener('lostpointercapture', stopTime);

  window.addEventListener('resize', resize);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopTime();
  });

  const teleport = (id: string): boolean => {
    const table: Record<string, { x: number; y: number }> = {
      entrance: OBJECTS.entrance,
      seedlings: OBJECTS.seedlings,
      mimosa: OBJECTS.mimosa,
      lithops: { x: 820, y: 650 },
      seed: OBJECTS.seed,
      flower: OBJECTS.flower,
      duck: { x: 2050, y: 930 },
      duckNest: OBJECTS.duckNest,
      microscope: OBJECTS.microscope,
      waste: { x: 2260, y: 930 }
    };
    const target = table[id];
    if (!target) return false;
    state.mode = 'world';
    app.classList.remove('microscope-mode');
    modelBadge.classList.remove('show');
    state.player.x = target.x;
    state.player.y = target.y + (id === 'duck' ? 0 : 110);
    state.player.vx = 0;
    state.player.vy = 0;
    state.camera.x = state.player.x;
    state.camera.y = state.player.y;
    return true;
  };

  const setClock = (id: ClockId, value: number): boolean => {
    state.clocks[id].active = true;
    state.clocks[id].t = clamp(value);
    return true;
  };

  function prepareQaScene(scene: 'mimosa' | 'duck' | 'flower' | 'microscope'): boolean {
    window.clearTimeout(hintTimer);
    state.evidenceQueue.length = 0;
    state.hintUntil = 0;
    softHint.classList.remove('show', 'concept');
    softHint.textContent = '';

    if (scene === 'mimosa') {
      returnToWorld();
      teleport('mimosa');
      state.mimosaTouchedAt = -99999;
      state.camera.x = state.camera.tx = OBJECTS.mimosa.x;
      state.camera.y = state.camera.ty = OBJECTS.mimosa.y + 65;
    } else if (scene === 'duck') {
      returnToWorld();
      setClock('duck', 1);
      state.duckWasteSeen = true;
      state.duck.swimmingAway = false;
      state.duck.x = state.duck.homeX;
      state.duck.y = state.duck.homeY;
      teleport('duck');
      state.camera.x = state.camera.tx = 2290;
      state.camera.y = state.camera.ty = 780;
      state.camera.z = state.camera.tz = Math.max(0.58, baseZoom() - 0.02);
    } else if (scene === 'flower') {
      returnToWorld();
      setClock('flower', 0.94);
      teleport('flower');
      state.camera.x = state.camera.tx = OBJECTS.flower.x;
      state.camera.y = state.camera.ty = OBJECTS.flower.y + 60;
    } else {
      (['nutrition', 'respiration', 'stimulus', 'growth', 'reproduction'] as EvidenceKey[]).forEach((key) => {
        state.evidence[key] = true;
      });
      enterMicroscope();
    }

    updateMissionUI();
    updateContextUI();
    renderer.draw(state, performance.now());
    return true;
  }

  if (location.hostname === '127.0.0.1' || location.hostname === 'localhost') {
    document.addEventListener('keydown', (event) => {
      const scenes: Record<string, 'mimosa' | 'duck' | 'flower' | 'microscope'> = {
        '1': 'mimosa',
        '2': 'duck',
        '3': 'microscope',
        '4': 'flower'
      };
      const scene = scenes[event.key];
      if (scene) prepareQaScene(scene);
    });
  }

  window.__LIFE_START__ = () => true;
  window.__LIFE_SMOKE__ = {
    snapshot: () => ({
      mode: state.mode,
      evidence: Object.keys(state.evidence),
      evidenceCount: coreEvidenceCount(state.evidence),
      position: { x: Math.round(state.player.x), y: Math.round(state.player.y) },
      clocks: Object.fromEntries(Object.entries(state.clocks).map(([key, value]) => [key, Number(value.t.toFixed(2))])),
      action: resolveAction()?.label || ''
    }),
    teleport,
    setClock,
    touchMimosa: () => {
      teleport('mimosa');
      state.mimosaTouchedAt = performance.now();
      queueEvidence('stimulus');
      return true;
    },
    unlockMicroscope: () => {
      (['nutrition', 'respiration', 'stimulus', 'growth', 'reproduction'] as EvidenceKey[]).forEach((key) => {
        state.evidence[key] = true;
      });
      teleport('microscope');
      updateMissionUI();
      updateContextUI();
      return resolveAction()?.kind === 'microscope';
    },
    openMicroscope: () => {
      (['nutrition', 'respiration', 'stimulus', 'growth', 'reproduction'] as EvidenceKey[]).forEach((key) => {
        state.evidence[key] = true;
      });
      enterMicroscope();
      return state.mode === 'microscope';
    },
    returnWorld: () => {
      returnToWorld();
      return true;
    },
    showScene: prepareQaScene
  };

  resize();
  updateMissionUI();
  requestAnimationFrame(frame);
}